import type { ScaleLinear, ScaleSymLog, ScaleTime } from 'd3-scale';

import type {
  SparklineDatapointMetadata,
  SparklineGapPolicy,
  SparklineVariant,
} from './types/sparkline-props.js';
import {
  barHeightAccessor,
  barWidthAccessor,
  barXAxisAccessor,
  barYAxisAccessor,
  definedAccessor,
  xAxisAccessor,
  y0Accessor,
  y1Accessor,
  yAccessor,
} from './utils/accessors.js';
import { shouldDisplayScatterPlot } from './utils/should-display-scatter-plot.js';
import type { SeriesCurve } from '../../types/timeseries.js';
import { AreaCanvas } from '../canvas/components/AreaCanvas.js';
import { BarCanvas } from '../canvas/components/BarCanvas.js';
import { LineCanvas } from '../canvas/components/LineCanvas.js';
import type {
  CanvasGradientGenerator,
  ShapeBuilderPayload,
} from '../canvas/utils/create-canvas-gradient.js';

interface SparklineDisplayProps {
  data: SparklineDatapointMetadata[];
  width: number;
  height: number;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number> | ScaleSymLog<number, number>;
  strokeColor: string;
  fillColor: string | CanvasGradientGenerator;
  variant: SparklineVariant;
  strokeWidth: number;
  curve?: SeriesCurve;
  radius?: number;
  gapPolicy: SparklineGapPolicy;
}

export const SparklineRenderer = ({
  data,
  width,
  height,
  xScale,
  yScale,
  fillColor,
  strokeColor,
  variant,
  strokeWidth,
  curve,
  radius,
  gapPolicy,
}: SparklineDisplayProps) => {
  const areaColor =
    typeof fillColor === 'string'
      ? () => fillColor
      : () =>
          ({ context }: ShapeBuilderPayload) =>
            fillColor({ context });

  return variant === 'area' ? (
    <AreaCanvas
      data={data}
      canvasWidth={width}
      canvasHeight={height}
      x={xAxisAccessor(xScale)}
      y1={y1Accessor(yScale)}
      y0={y0Accessor(yScale)}
      defined={definedAccessor}
      color={areaColor}
      displayScatterPlot={shouldDisplayScatterPlot(gapPolicy)}
      lineColor={() => strokeColor}
      curve={curve}
    />
  ) : variant === 'line' ? (
    <LineCanvas
      data={data}
      canvasWidth={width}
      canvasHeight={height}
      x={xAxisAccessor(xScale)}
      y={yAccessor(yScale)}
      defined={definedAccessor}
      color={() => strokeColor}
      displayScatterPlot={shouldDisplayScatterPlot(gapPolicy)}
      strokeWidth={strokeWidth}
      curve={curve}
      radius={radius}
    />
  ) : (
    <BarCanvas
      data={data}
      canvasWidth={width}
      canvasHeight={height}
      x={barXAxisAccessor(xScale)}
      y={barYAxisAccessor(yScale)}
      width={(point) => barWidthAccessor(point, xScale)}
      height={(point) => barHeightAccessor(point, yScale)}
      strokeWidth={1}
      color={() => strokeColor}
      opacity={() => 1}
    />
  );
};
